import React, { useCallback, useState } from 'react';
import { Alert, Box, Button, Skeleton, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import ErrorBoundary from '../components/ErrorBoundary';
import ActiveProjectsPanel from '../components/commercial/ActiveProjectsPanel';
import ActivityStrip from '../components/commercial/ActivityStrip';
import DashboardFilterBar from '../components/commercial/DashboardFilterBar';
import KpiStrip from '../components/commercial/KpiStrip';
import PipelineRail from '../components/commercial/PipelineRail';
import ProposalsPanel from '../components/commercial/ProposalsPanel';
import ResultsPanel from '../components/commercial/ResultsPanel';
import StageValuePanel from '../components/commercial/StageValuePanel';
import TodoPanel from '../components/commercial/TodoPanel';
import { NAUTILUS, useConsolePalette } from '../components/commercial/consolePalette';
import { useCommercialDashboard } from '../hooks/useCommercialDashboard';
import { OpportunityPipelineStatus } from '../types';
import { CommercialDashboardFilters, dashboardMatchesFilters } from '../utils/commercialDashboard';
import { isPipelineStatus } from '../utils/commercialPlanning';
import { leadsPageUrl } from '../utils/opportunityTable';

const pairSx = (columns: string) => ({
  display: 'grid',
  gap: 2,
  gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: columns },
  alignItems: 'stretch',
});

const DashboardHeader: React.FC = () => {
  const palette = useConsolePalette();
  return (
    <Box>
      <Typography
        variant="h4"
        component="h1"
        sx={{ fontWeight: 600, letterSpacing: '-0.01em', color: palette.isDark ? '#ffffff' : NAUTILUS.deepOcean }}
      >
        Commercial Dashboard
      </Typography>
      <Typography variant="body2" sx={{ color: palette.inkSoft }}>
        Where every lead stands, what needs chasing, and what is already in execution.
      </Typography>
    </Box>
  );
};

const DashboardSkeleton: React.FC = () => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }} aria-busy="true" aria-label="Loading commercial dashboard">
    <Skeleton variant="rounded" height={64} />
    <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)', lg: 'repeat(6, 1fr)' } }}>
      {Array.from({ length: 6 }, (_, index) => <Skeleton key={index} variant="rounded" height={112} />)}
    </Box>
    <Box sx={pairSx('7fr 5fr')}>
      <Skeleton variant="rounded" height={300} />
      <Skeleton variant="rounded" height={300} />
    </Box>
    <Box sx={pairSx('1fr 1fr')}>
      <Skeleton variant="rounded" height={320} />
      <Skeleton variant="rounded" height={320} />
    </Box>
  </Box>
);

const CommercialDashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const [filters, setFilters] = useState<CommercialDashboardFilters>({});
  const { data, error, refreshing, refresh } = useCommercialDashboard(filters);

  const openLeads = useCallback(
    (status?: OpportunityPipelineStatus) =>
      navigate(leadsPageUrl(status && isPipelineStatus(status) ? { status } : {})),
    [navigate],
  );
  const openLead = useCallback(
    (opportunityId: number) => navigate(leadsPageUrl({ leadId: String(opportunityId) })),
    [navigate],
  );
  const openProject = useCallback((projectId: number) => navigate(`/projects/${projectId}/documents`), [navigate]);

  if (!data) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <DashboardHeader />
        {error ? (
          <Alert
            severity="error"
            action={<Button color="inherit" size="small" onClick={() => void refresh()}>Retry</Button>}
          >
            {error}
          </Alert>
        ) : (
          <DashboardSkeleton />
        )}
      </Box>
    );
  }

  const stale = !dashboardMatchesFilters(data, filters);

  return (
    <ErrorBoundary>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pb: 3 }}>
        <DashboardHeader />

        <DashboardFilterBar
          dashboard={data}
          filters={filters}
          onChange={setFilters}
          refreshing={refreshing}
          error={error}
          onRefresh={() => void refresh()}
        />

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            opacity: stale ? 0.55 : 1,
            transition: 'opacity 150ms ease-out',
          }}
          aria-busy={stale}
        >
          <KpiStrip dashboard={data} onNavigate={navigate} />

          <Box sx={pairSx('minmax(0, 7fr) minmax(0, 5fr)')}>
            <PipelineRail dashboard={data} onOpenLeads={openLeads} />
            <TodoPanel dashboard={data} onOpenLeads={openLeads} onOpenLead={openLead} />
          </Box>

          <Box sx={pairSx('minmax(0, 1fr) minmax(0, 1fr)')}>
            <StageValuePanel dashboard={data} />
            <ProposalsPanel dashboard={data} onOpenProject={openProject} />
          </Box>

          <Box sx={pairSx('minmax(0, 5fr) minmax(0, 7fr)')}>
            <ResultsPanel dashboard={data} />
            <ActiveProjectsPanel dashboard={data} onOpenProject={openProject} />
          </Box>

          <ActivityStrip dashboard={data} onOpenLead={openLead} />
        </Box>
      </Box>
    </ErrorBoundary>
  );
};

export default CommercialDashboardPage;
